import { useState } from "react";
import { formatDate, formatDateTime } from "@/lib/format";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { BackLink } from "@/components/BackLink";
import {
  usePlatformForceAnchor,
  usePlatformOrgDetail,
  useResendPlatformOrgInvite,
  usePlatformSetAnchorPolicy,
} from "@/lib/platform";
import type { PlatformOrgDetail } from "@/lib/types";
import { useOrgSupportTickets } from "@/lib/support";
import { TicketStatusPill } from "@/modules/support/ticketStatus";
import { KycStatusModal } from "@/modules/platform/KycStatusModal";
import { SanctionsOverrideModal } from "@/modules/platform/SanctionsOverrideModal";
import { SuspendOrgModal } from "@/modules/platform/SuspendOrgModal";

export default function PlatformOrgDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const detail = usePlatformOrgDetail(id);
  const [editing, setEditing] = useState<
    "kyc" | "suspend" | "reinstate" | "sanctions" | null
  >(null);

  if (detail.isLoading) return <div className="text-sm text-nb-mute">{t("common.loading")}</div>;
  if (detail.isError || !detail.data) {
    return (
      <div className="text-sm text-nb-danger">{t("platform.org.notFound")}</div>
    );
  }
  const d = detail.data;

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <BackLink fallback="/platform/orgs" className="text-xs text-nb-mute hover:underline">
            {t("platform.org.back")}
          </BackLink>
          <h1 className="mt-1 text-2xl font-semibold">{d.name}</h1>
          {d.legal_name && (
            <div className="text-sm text-nb-mute">{d.legal_name}</div>
          )}
        </div>
        <div className="flex flex-wrap items-start gap-2">
          <ResendOrgInviteButton orgId={d.id} />
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setEditing("kyc")}
            data-testid="open-kyc-modal"
          >
            {t("platform.org.changeKyb")}
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={() => setEditing("suspend")}
            data-testid="open-suspend-modal"
          >
            {t("platform.org.suspend")}
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => setEditing("reinstate")}
          >
            {t("platform.org.reinstate")}
          </button>
        </div>
      </div>

      <Section title={t("platform.org.secIdentity")}>
        <KV label={t("platform.orgs.col.country")} value={d.country} />
        <KV
          label={t("platform.orgs.col.created")}
          value={d.created_at ? formatDateTime(d.created_at) : "—"}
        />
        <KV
          label={t("platform.org.kybStatus")}
          value={t(`platform.orgs.filter.${d.kyc_status}`, d.kyc_status)}
        />
        <KV
          label={t("platform.org.kybAttested")}
          value={
            d.kyc_attested_at
              ? formatDateTime(d.kyc_attested_at)
              : "—"
          }
        />
      </Section>

      <Section title={t("platform.org.secKybDetails")}>
        <KV label={t("platform.org.vatId")} value={d.vat_id || "—"} />
        <KV label={t("platform.org.vatName")} value={d.vat_validated_name || "—"} />
        <KV
          label={t("platform.org.regJurisdiction")}
          value={d.registration_jurisdiction || "—"}
        />
        <KV
          label={t("platform.org.regNumber")}
          value={d.registration_number || "—"}
        />
        <KV
          label={t("platform.org.registryName")}
          value={d.registry_validated_name || "—"}
        />
        <KV
          label={t("platform.org.registryActive")}
          value={d.registry_is_active ? t("platform.orgs.filter.active") : t("platform.org.inactive")}
        />
      </Section>

      <Section title={t("platform.org.secSanctions")}>
        <div className="col-span-2 flex items-start justify-between gap-4">
          <div className="flex-1">
            {d.sanctions_match_found ? (
              <div className="rounded-md border border-nb-danger/40 bg-nb-danger/5 p-3 text-sm">
                <div className="font-semibold text-nb-danger">{t("platform.orgs.flagged")}</div>
                <div className="text-xs text-nb-mute">
                  {t("platform.org.lastScreened", { date: d.sanctions_screened_at || "—" })}
                </div>
                {d.sanctions_matches.length > 0 && (
                  <pre className="mt-2 overflow-x-auto rounded bg-nb-bg p-2 text-xs">
                    {JSON.stringify(d.sanctions_matches, null, 2)}
                  </pre>
                )}
              </div>
            ) : (
              <div className="text-sm text-nb-mute">
                {t("platform.org.cleanAsOf", {
                  date: d.sanctions_screened_at
                    ? formatDate(d.sanctions_screened_at)
                    : t("platform.org.neverScreened"),
                })}
              </div>
            )}
            {d.sanctions_force_clean_until && (
              <div
                className="mt-2 rounded-md border border-nb-warn/40 bg-nb-warn/5 p-2 text-xs"
                data-testid="sanctions-override-active"
              >
                {t("platform.org.overrideActive", {
                  date: formatDateTime(d.sanctions_force_clean_until),
                })}
              </div>
            )}
          </div>
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={() => setEditing("sanctions")}
            data-testid="open-sanctions-override-modal"
          >
            {d.sanctions_force_clean_until ? t("platform.org.editOverride") : t("platform.org.override")}
          </button>
        </div>
      </Section>

      <AnchorPolicySection orgId={d.id} detail={d} />

      <ForceAnchorSection />

      <Section title={t("platform.org.secSubscription")}>
        {d.subscription ? (
          <>
            <KV label={t("platform.org.plan")} value={d.subscription.plan_slug} />
            <KV label={t("platform.common.status")} value={d.subscription.status} />
            <KV label={t("platform.org.operators")} value={t("platform.org.unlimited")} />
            <KV
              label={t("platform.org.periodEnds")}
              value={d.subscription.current_period_end || "—"}
            />
          </>
        ) : (
          <div className="text-sm text-nb-mute">{t("platform.org.noSubscription")}</div>
        )}
        <KV label={t("platform.org.openUsage")} value={String(d.open_usage_minutes)} />
        <KV label={t("platform.org.activeMembers")} value={String(d.member_count)} />
        <KV label={t("platform.org.sessionsTotal")} value={String(d.session_count)} />
        <KV
          label={t("platform.org.openSessions")}
          value={String(d.open_session_count)}
        />
      </Section>

      <SupportTicketsSection orgId={id} />

      {editing === "kyc" && (
        <KycStatusModal
          orgId={d.id}
          currentStatus={d.kyc_status}
          onClose={() => setEditing(null)}
        />
      )}
      {(editing === "suspend" || editing === "reinstate") && (
        <SuspendOrgModal
          orgId={d.id}
          orgName={d.name}
          mode={editing}
          onClose={() => setEditing(null)}
        />
      )}
      {editing === "sanctions" && (
        <SanctionsOverrideModal
          orgId={d.id}
          orgName={d.name}
          currentUntil={d.sanctions_force_clean_until}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function AnchorPolicySection({
  orgId,
  detail,
}: {
  orgId: string;
  detail: PlatformOrgDetail;
}) {
  const { t } = useTranslation();
  const setPolicy = usePlatformSetAnchorPolicy(orgId);
  return (
    <section
      className="rounded-md border border-nb-border bg-nb-surface p-4"
      aria-labelledby="anchor-policy-heading"
    >
      <h2
        id="anchor-policy-heading"
        className="mb-2 text-sm font-semibold uppercase tracking-wide text-nb-mute"
      >
        {t("platform.org.anchorPolicyHeading")}
      </h2>
      <p className="mb-3 text-xs text-nb-mute">
        {t("platform.org.anchorPolicyDesc")}
      </p>

      {/* Immediate blockchain + eIDAS toggle */}
      <div className="flex items-start justify-between gap-4 border-b border-nb-border py-3">
        <div className="flex-1">
          <div className="text-sm font-medium">
            {t("platform.org.immediateBlockchain")}
          </div>
          <div className="text-xs text-nb-mute">
            {detail.blockchain_immediate
              ? t("platform.org.immediateBlockchainOn")
              : t("platform.org.immediateBlockchainOff")}
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={detail.blockchain_immediate}
          disabled={setPolicy.isPending}
          onClick={() =>
            setPolicy.mutate({
              blockchain_immediate: !detail.blockchain_immediate,
            })
          }
          data-testid="toggle-blockchain-immediate"
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${
            detail.blockchain_immediate ? "bg-nb-mint" : "bg-nb-border"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
              detail.blockchain_immediate ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      {/* eIDAS scope */}
      <div className="flex items-start justify-between gap-4 py-3">
        <div className="flex-1">
          <div className="text-sm font-medium">
            {t("platform.org.eidasScope")}
          </div>
          <div className="text-xs text-nb-mute">
            {t("platform.org.eidasScopeDesc")}
          </div>
        </div>
        <select
          value={detail.eidas_scope}
          disabled={setPolicy.isPending}
          onChange={(e) =>
            setPolicy.mutate({
              eidas_scope: e.target.value as "final_only" | "everything",
            })
          }
          data-testid="select-eidas-scope"
          className="rounded-md border border-nb-border bg-nb-bg p-2 text-sm"
        >
          <option value="final_only">
            {t("platform.org.eidasFinalOnly")}
          </option>
          <option value="everything">
            {t("platform.org.eidasEverything")}
          </option>
        </select>
      </div>

      {setPolicy.isError && (
        <div className="mt-1 text-xs text-nb-danger" role="alert">
          {t("platform.org.anchorPolicyError")}
        </div>
      )}
    </section>
  );
}


function ForceAnchorSection() {
  const { t } = useTranslation();
  const [sessionId, setSessionId] = useState("");
  const force = usePlatformForceAnchor();
  return (
    <section
      className="rounded-md border border-nb-border bg-nb-surface p-4"
      aria-labelledby="force-anchor-heading"
    >
      <h2
        id="force-anchor-heading"
        className="mb-2 text-sm font-semibold uppercase tracking-wide text-nb-mute"
      >
        {t("platform.org.forceHeading")}
      </h2>
      <p className="mb-3 text-xs text-nb-mute">
        {t("platform.org.forceDesc")}
      </p>
      <form
        className="flex flex-wrap items-center gap-2 text-sm"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!sessionId.trim()) return;
          await force.mutateAsync(sessionId.trim());
        }}
      >
        <input
          value={sessionId}
          onChange={(e) => setSessionId(e.target.value)}
          placeholder={t("platform.org.forcePlaceholder")}
          className="flex-1 min-w-[240px] rounded-md border border-nb-border bg-nb-bg p-2 font-mono text-xs"
          data-testid="force-anchor-input"
        />
        <button
          type="submit"
          className="btn-secondary"
          disabled={!sessionId.trim() || force.isPending}
          data-testid="force-anchor-submit"
        >
          {force.isPending ? t("platform.common.anchoring") : t("platform.org.forceSubmit")}
        </button>
      </form>
      {force.isError && (
        <div className="mt-2 text-xs text-nb-danger" role="alert">
          {t("platform.org.forceError")}
        </div>
      )}
      {force.isSuccess && force.data && (
        <div className="mt-2 text-xs text-nb-ok" role="status">
          {t("platform.org.forceSuccess", {
            count: force.data.tokens.length,
            detail: force.data.tokens
              .map((tok) => `${tok.backend}:${tok.state}`)
              .join(", "),
          })}
        </div>
      )}
    </section>
  );
}

function SupportTicketsSection({ orgId }: { orgId: string | undefined }) {
  const { t } = useTranslation();
  const tickets = useOrgSupportTickets(orgId);
  return (
    <Section title={t("platform.org.secSupport")}>
      {tickets.isLoading ? (
        <div className="text-sm text-nb-mute">{t("common.loading")}</div>
      ) : !tickets.data?.length ? (
        <div className="text-sm text-nb-mute">{t("platform.org.noSupportTickets")}</div>
      ) : (
        <ul className="divide-y divide-nb-border">
          {tickets.data.map((tk) => (
            <li key={tk.id} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <Link
                  to={`/support-console/tickets/${tk.id}`}
                  className="text-sm text-nb-text hover:text-nb-mint"
                >
                  {tk.subject}
                </Link>
                <div className="text-xs text-nb-mute">
                  #{tk.id.slice(0, 8)} · {tk.support_plan_slug} ·{" "}
                  {tk.assignee_email
                    ? t("support.console.ticket.assignedTo", { who: tk.assignee_email })
                    : t("support.console.stats.unassigned")}
                </div>
              </div>
              <TicketStatusPill status={tk.status} />
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function ResendOrgInviteButton({ orgId }: { orgId: number | string }) {
  const { t } = useTranslation();
  const resend = useResendPlatformOrgInvite();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [err, setErr] = useState(false);
  return (
    <div className="flex flex-col items-start">
      <button
        type="button"
        className="btn-secondary"
        disabled={resend.isPending}
        onClick={() => {
          setErr(false);
          setSentTo(null);
          resend.mutate(orgId, {
            onSuccess: (res) => setSentTo(res.email),
            onError: () => setErr(true),
          });
        }}
        data-testid="resend-account-email"
      >
        {t("platform.org.resendAccountEmail")}
      </button>
      {sentTo && (
        <span className="mt-1 text-xs text-nb-ok" role="status">
          {t("platform.org.resendSent", { email: sentTo })}
        </span>
      )}
      {err && (
        <span className="mt-1 text-xs text-nb-danger" role="alert">
          {t("platform.org.resendError")}
        </span>
      )}
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-md border border-nb-border bg-nb-surface p-4">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-nb-mute">
        {title}
      </h2>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
        {children}
      </dl>
    </section>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <dt className="text-xs text-nb-mute">{label}</dt>
      <dd className="text-sm">{value}</dd>
    </div>
  );
}
